// ApiService.js
// Singleton class for API requests with network listeners, queuing, retries, deduplication, and event emission.
import axios from 'axios';
import EventEmitter from 'eventemitter3';
import RequestTracker from './RequestTracker';
import RetryManager from './RetryManager';
import TokenManager from './TokenManager';
import setupInterceptors from './Interceptors';

class ApiService {
    constructor() {
        if (!ApiService.instance) {
            this.axiosInstance = axios.create();
            this.token = null;
            this.pendingRequests = new Map();
            this.retryManager = new RetryManager(this);
            this.isOnline = navigator.onLine;
            this.maxRetries = 3;
            this.retryDelay = 1000; // Default retry delay in ms
            this.defaultTimeout = 10000; // Default timeout in ms
            this.abortControllers = new Map();
            this.emitter = new EventEmitter();
            this._setupNetworkListeners();
            setupInterceptors(this.axiosInstance, this);
            ApiService.instance = this;
        }
        return ApiService.instance;
    }

    on(event, handler) {
        this.emitter.on(event, handler);
    }
    off(event, handler) {
        this.emitter.off(event, handler);
    }
    emit(event, payload) {
        this.emitter.emit(event, payload);
    }

    _setupNetworkListeners() {
        window.addEventListener('online', () => {
            this.isOnline = true;
            // Update all queued_offline requests to queued_network
            for (const [key, { meta }] of this.pendingRequests.entries()) {
                if (meta.status === 'queued_offline') {
                    meta.status = 'queued_network';
                    this.emit('status', { key, meta });
                }
                if (meta.status === 'waiting_token_queued_offline') {
                    meta.status = 'waiting_token';
                    this.emit('status', { key, meta });
                }
            }
            this.emit('network', { status: 'online' });
            this.retryManager.retryAll();
        });
        window.addEventListener('offline', () => {
            this.isOnline = false;
            // Update all queued_network requests to queued_offline
            for (const [key, { meta }] of this.pendingRequests.entries()) {
                if (meta.status === 'queued_network') {
                    meta.status = 'queued_offline';
                    this.emit('status', { key, meta });
                }
                if (meta.status === 'waiting_token') {
                    meta.status = 'waiting_token_queued_offline';
                    this.emit('status', { key, meta });
                }
            }
            this.emit('network', { status: 'offline' });
        });
    }

    setToken(token) {
        this.token = token;
        if (typeof this.onTokenUpdate === 'function') {
            this.onTokenUpdate(token);
        }
    }

    setTokenUpdateCallback(callback) {
        this.onTokenUpdate = callback;
    }

    request(config) {
        const { url, method = 'get', data, params, headers = {}, meta = {} } = config;
        const key = this._getRequestKey(url, method, data, params);
        if (this.pendingRequests.has(key)) {
            console.log("RETURNING THE SAME REQUEST");
            return this.pendingRequests.get(key).meta;
        }
        const tracker = new RequestTracker({ url, method, data, params });
        const abortController = new AbortController();
        this.abortControllers.set(key, abortController);
        let status = this.isOnline ? 'pending' : 'queued_offline';
        if (TokenManager.refreshing) {
            status = 'waiting_token';
        }
        // Allow per-request override of retryDelay and timeout
        const retryDelay = config.retryDelay !== undefined ? config.retryDelay : this.retryDelay;
        const timeout = config.timeout !== undefined ? config.timeout : this.defaultTimeout;
        const metaObj = {
            ...tracker.getMeta(),
            status,
            retries: 0,
            abort: () => this.abortRequest(key),
            retryDelay, // Attach to meta for use in _sendRequest
            timeout,    // Attach to meta for use in _sendRequest
        };
        this.pendingRequests.set(key, { meta: metaObj, abortController });
        this.emit('status', { key, meta: metaObj });
        // Always return metaObj, never a Promise, even when queuing
        if (!this.isOnline) {
            metaObj.status = 'queued_offline';
            this.retryManager.queueRequest({ config, key, tracker, metaObj });
            return metaObj;
        }
        if (TokenManager.refreshing) {
            metaObj.status = 'waiting_token';
            this.retryManager.queueRequest({ config, key, tracker, metaObj });
            return metaObj;
        }
        this._sendRequest({ config, key, tracker, metaObj });
        return metaObj;
    }

    async _sendRequest({ config, key, tracker, metaObj }) {
        try {
            const response = await this.axiosInstance({
                ...config,
                headers: {
                    ...config.headers,
                    Authorization: config.headers?.Authorization || this.token,
                },
                signal: this.abortControllers.get(key).signal,
                timeout: metaObj.timeout,
            });
            tracker.markComplete('success');
            const lastAttempt = tracker.attempts[tracker.attempts.length - 1];
            metaObj.status = lastAttempt.status;
            metaObj.endTime = lastAttempt.endTime;
            metaObj.attempts = [...tracker.attempts];
            this.emit('status', { key, meta: metaObj, response });
            this.pendingRequests.delete(key);
            this.abortControllers.delete(key);
            // Clear any pending retry timeout
            if (metaObj.retryTimeoutId) {
                clearTimeout(metaObj.retryTimeoutId);
                metaObj.retryTimeoutId = null;
            }
            return response;
        } catch (error) {
            console.error("API Request failed:", error);
            /**Avoid retry for certain error codes/messages (short explanation for easy reference)
             * 400 - Bad Request: The request is malformed or invalid. Retrying won’t help because the client must fix the request.
             * 401 - Unauthorized: The request lacks valid authentication credentials. Retrying without new credentials will not succeed.
             * 403 - Forbidden: The server understood the request but refuses to authorize it. Retrying is pointless unless permissions change.
             * 404 - Not Found: Request reached the server and the requested resource (endpoint) does not exist. Retrying will not help unless the resource appears later.
             * 422 - Unprocessable Entity: The server understands the request but cannot process it due to semantic errors (e.g., validation errors). The client must correct the data.
             * 
             * 'invalid': Indicates something is wrong with the request data (e.g., "invalid token", "invalid input").
             * 'not found': The resource does not exist (e.g., "user not found").
             * 'forbidden': The user is not allowed to perform the action.
             * 'unauthorized': The user is not authenticated or lacks permission.
             * 'unprocessable': The server cannot process the request, often due to validation errors.*/

            const noRetryCodes = [400, 401, 403, 404, 422];
            const noRetryMessages = [
                'invalid', 'not found', 'forbidden', 'unauthorized', 'unprocessable',
            ];
            const status = error?.response?.status;
            const message = error?.response?.data?.message?.toLowerCase?.() || '';
            if (
                noRetryCodes.includes(status) ||
                noRetryMessages.some(msg => message.includes(msg))
            ) {
                tracker.markComplete('failed');
                const lastAttemptFail = tracker.attempts[tracker.attempts.length - 1];
                metaObj.status = lastAttemptFail.status;
                metaObj.endTime = lastAttemptFail.endTime;
                metaObj.attempts = [...tracker.attempts];
                this.emit('status', { key, meta: metaObj, error });
                this.pendingRequests.delete(key);
                this.abortControllers.delete(key);
                return;
            }
            if (!this.isOnline) {
                metaObj.status = 'queued_offline';
                this.retryManager.queueRequest({ config, key, tracker, metaObj });
            } else if (metaObj.retries < this.maxRetries) {
                tracker.addRetry();
                metaObj.status = 'retry';
                metaObj.retries++;
                metaObj.attempts = [...tracker.attempts];
                this.emit('status', { key, meta: metaObj, error });
                // Store timeout id for possible clearing
                metaObj.retryTimeoutId = setTimeout(() => this._sendRequest({ config, key, tracker, metaObj }), metaObj.retryDelay);
            } else {
                tracker.markComplete('failed');
                const lastAttemptFinal = tracker.attempts[tracker.attempts.length - 1];
                metaObj.status = lastAttemptFinal.status;
                metaObj.endTime = lastAttemptFinal.endTime;
                metaObj.attempts = [...tracker.attempts];
                this.emit('status', { key, meta: metaObj, error });
                this.pendingRequests.delete(key);
                this.abortControllers.delete(key);
                // Clear any pending retry timeout
                if (metaObj.retryTimeoutId) {
                    clearTimeout(metaObj.retryTimeoutId);
                    metaObj.retryTimeoutId = null;
                }
            }
        }
    }

    abortRequest(key, skipRetryManager = false) {
        if (this.abortControllers.has(key)) {
            this.abortControllers.get(key).abort();
            this.abortControllers.delete(key);
            // Clear any pending retry timeout
            const req = this.pendingRequests.get(key);
            if (req && req.meta && req.meta.retryTimeoutId) {
                clearTimeout(req.meta.retryTimeoutId);
                req.meta.retryTimeoutId = null;
            }
            this.pendingRequests.delete(key);
            //   if (!skipRetryManager && this.retryManager && typeof this.retryManager.removeRequest === 'function') {
            //     this.retryManager.removeRequest(key);
            //   }
            if (!skipRetryManager) {
                this.retryManager.removeRequest(key);
            }
            this.emit('status', { key, meta: { status: 'aborted' } });
        }
    }

    abortAll() {
        for (const key of Array.from(this.abortControllers.keys())) {
            this.abortRequest(key, true);
        }
        // if (this.retryManager && typeof this.retryManager.clearAll === 'function') {
        //   this.retryManager.clearAll();
        // }
        this.retryManager.clearAll();
    }

    /**
     * Returns a unique string key for a request based on url, method, data, and params.
     * Used for deduplication, aborting, and tracking requests.
     */
    _getRequestKey(url, method, data, params) {
        return JSON.stringify({ url, method, data, params });
    }
}

const instance = new ApiService();
export default instance;
